// src/data/memberFile.js
//
// Track 6 item 6a-06 (2026-09-15, task list section 250). Requirements SS6.1, Bob's words: "Every
// claim as a separate claim in that member's digital file folder... every discipline case as its own
// collection." - "The organising unit is THE MEMBER, not the claim. Today the platform is
// claim-centric: a claim knows its claimant. This inverts it - a member has a file, the file contains
// matters, each matter is a claim or a discipline case, and each carries its own documents. This is
// how a Local Chairperson actually holds a caseload."
//
// HOW THE INVERSION IS BUILT, AND WHY THIS WAY (the assistant's design under ruling 124, recorded as
// ruling 132 by delegation, withdrawable by number). The file is DERIVED, not stored. A claim keeps
// knowing its claimant (createdBy, claimantName, claimantEmail); a discipline case keeps knowing its
// member (memberEmail, memberName, createdBy); the member's file is the set of matters keyed to him,
// read through the queries the officer already has (claimQueries / the case list) and grouped here.
// A stored members/{uid}/matters copy would put every matter in two places - the fact-in-two-places
// defect of directives SS XI - and would touch the eleven claim write sites for nothing the officer
// can see. The 26 August count (8 files, 30 call sites) was the price of that copy; this pays none of
// it and the file reads the same.
//
// IDENTITY. A member is one person, and the record names him three ways. The key is the ACCOUNT
// (uid) when a matter carries one (a claim's createdBy is the claimant's account); otherwise the
// e-mail address, lower-cased; otherwise the name alone - and the file SAYS which, because a file
// joined by name is a weaker record than one joined by account. Nothing is merged by guess: two
// keys are two files until an officer has a fact that joins them.
//
// Pure: no React, no Firestore. The page hands in what it can already read.

const norm = (s) => String(s || '').trim().toLowerCase();

// A claim's createdBy is the ACCOUNT THAT FILED IT (claimPayload: "the ownership authority"). That is
// the member's account when he filed for himself - filedBy (his e-mail) equals claimantEmail, or the
// claim names no other claimant e-mail. An officer filing on a member's behalf is createdBy too, and
// then the member is keyed by his e-mail or name, never by the officer's account.
export function memberKeyOfClaim(c) {
  if (!c) return null;
  const filedSelf = c.createdBy && (!norm(c.claimantEmail) || norm(c.claimantEmail) === norm(c.filedBy));
  if (filedSelf) return { key: 'uid:' + c.createdBy, by: 'account', uid: c.createdBy, email: norm(c.claimantEmail) || norm(c.filedBy) || null, name: c.claimantName || null };
  if (norm(c.claimantEmail)) return { key: 'email:' + norm(c.claimantEmail), by: 'email', uid: null, email: norm(c.claimantEmail), name: c.claimantName || null };
  if (c.claimantName) return { key: 'name:' + norm(c.claimantName), by: 'name', uid: null, email: null, name: c.claimantName };
  return null;
}

export function memberKeyOfCase(k) {
  if (!k) return null;
  if (k.memberUid) return { key: 'uid:' + k.memberUid, by: 'account', uid: k.memberUid, email: norm(k.memberEmail) || null, name: k.memberName || null };
  if (norm(k.memberEmail)) return { key: 'email:' + norm(k.memberEmail), by: 'email', uid: null, email: norm(k.memberEmail), name: k.memberName || null };
  if (k.memberName) return { key: 'name:' + norm(k.memberName), by: 'name', uid: null, email: null, name: k.memberName };
  return null;
}

const dayOf = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return null;
};

// The filing window is READ here for display and keyed `filingWindow`: WIN-1 in ycp-verify.js reads any
// key spelled like the claim's own field as a writer of it, and the only writer is computeExpiresAt().
function matterOfClaim(c) {
  return {
    kind: 'claim', id: c.docId || c.id, number: c.id || c.docId, title: c.claimBasis || c.violationType || c.description || '(claim)',
    status: c.status || null, on: dayOf(c.filedAt) || dayOf(c.createdAt), updated: dayOf(c.updatedAt) || dayOf(c.filedAt) || dayOf(c.createdAt),
    committee: c.committee || c.generalCommittee || null, scopeKey: c.scopeKey || null, railroad: c.railroad || null, agreement: c.applicableAgreement || null,
    documents: Array.isArray(c.documents) ? c.documents.length : 0, outcome: c.outcome || null, filingWindow: dayOf(c.expiresAt),
    isDraft: c.status === 'draft',
  };
}
function matterOfCase(k) {
  return {
    kind: 'discipline', id: k.id, number: k.caseNumber || k.id, title: (k.chargeSummary || k.charge || k.offense || 'discipline case'),
    status: k.status || null, on: dayOf(k.noticeDate) || dayOf(k.openedAt), updated: dayOf(k.updatedAt) || dayOf(k.openedAt),
    committee: k.generalCommittee || null, scopeKey: k.scopeKey || null, railroad: k.railroad || null, agreement: k.applicableAgreement || null,
    documents: Array.isArray(k.documents) ? k.documents.length : 0, outcome: k.decision || null, hearingEvents: Array.isArray(k.hearingEvents) ? k.hearingEvents.length : 0,
    isDraft: false,
  };
}

// The files: one per member key, matters newest first. Drafts are the author's alone (ruling 56)
// and are counted, never listed, in anyone's file but his own (`viewerUid`).
export function buildMemberFiles(claims, cases, viewerUid) {
  const files = new Map();
  const touch = (k) => {
    if (!files.has(k.key)) files.set(k.key, { key: k.key, by: k.by, uid: k.uid, email: k.email, name: k.name, matters: [], draftsHidden: 0, railroads: new Set(), committees: new Set() });
    const f = files.get(k.key);
    if (!f.name && k.name) f.name = k.name;
    if (!f.email && k.email) f.email = k.email;
    return f;
  };
  for (const c of (Array.isArray(claims) ? claims : [])) {
    const k = memberKeyOfClaim(c); if (!k) continue;
    const f = touch(k);
    const m = matterOfClaim(c);
    if (m.isDraft && c.createdBy !== viewerUid) { f.draftsHidden++; continue; }
    f.matters.push(m);
    if (m.railroad) f.railroads.add(m.railroad); if (m.committee) f.committees.add(m.committee);
  }
  for (const k0 of (Array.isArray(cases) ? cases : [])) {
    const k = memberKeyOfCase(k0); if (!k) continue;
    const f = touch(k);
    const m = matterOfCase(k0);
    f.matters.push(m);
    if (m.railroad) f.railroads.add(m.railroad); if (m.committee) f.committees.add(m.committee);
  }
  const out = Array.from(files.values()).map(f => {
    f.matters.sort((a, b) => String(b.on || '') < String(a.on || '') ? -1 : String(b.on || '') > String(a.on || '') ? 1 : 0);
    const open = f.matters.filter(m => !['closure', 'archived', 'closed', 'decided'].includes(m.status) && !m.outcome).length;
    return {
      key: f.key, by: f.by, uid: f.uid, email: f.email, name: f.name || f.email || '(unnamed)',
      matters: f.matters, claims: f.matters.filter(m => m.kind === 'claim').length, cases: f.matters.filter(m => m.kind === 'discipline').length, open, draftsHidden: f.draftsHidden,
      documents: f.matters.reduce((n, m) => n + (m.documents || 0), 0),
      lastActivity: f.matters.reduce((d, m) => (m.updated && (!d || m.updated > d)) ? m.updated : d, null),
      railroads: Array.from(f.railroads).sort(), committees: Array.from(f.committees).sort(),
      joinedBy: f.by === 'account' ? 'account' : f.by === 'email' ? 'e-mail address (no account on the matters)' : 'NAME ONLY - the weakest join; two people with one name would share this file',
    };
  });
  out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return out;
}

// Files that may be one person under two keys: the same e-mail on an account-keyed file and an
// e-mail-keyed file, or the same name on two files. Reported, never merged.
export function possibleDuplicates(files) {
  const list = Array.isArray(files) ? files : [];
  const out = [];
  for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) {
    const a = list[i], b = list[j];
    if (a.email && a.email === b.email) out.push({ a: a.key, b: b.key, why: 'same e-mail address ' + a.email });
    else if (a.name && b.name && norm(a.name) === norm(b.name) && a.name !== '(unnamed)') out.push({ a: a.key, b: b.key, why: 'same name ' + a.name });
  }
  return out;
}

export function searchFiles(files, text) {
  const t = norm(text);
  if (!t) return files;
  return (files || []).filter(f => norm(f.name).includes(t) || (f.email && f.email.includes(t)) || f.matters.some(m => norm(m.number).includes(t)));
}
